"use client"
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { handleNavigationWithPaywall } from "@/utils/paywall-navigation-utils";

import { ChevronRight } from "lucide-react"
import { isMenuItemActive, isGroupActive } from "@/utils/sidebar-utils"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileAlt } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid"
import { SidebarTooltip } from "@/components/ui/sidebar-tooltip"

import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuAction,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from "@/components/ui/sidebar"

function ManagementSystem({
    items,
}: {
    items: {
        title: string
        url: string
        routeName?: string
        shortTitle?: string
        icon?: React.ComponentType<any>
        isActive?: boolean
        items?: {
            title: string
            shortTitle?: string
            url: string
            routeName?: string
            icon?: React.ComponentType<any>
            items?: {
                title: string
                shortTitle?: string
                url: string
                routeName?: string
                icon?: React.ComponentType<any>
            }[]
        }[]
    }[]
}) {
    // Debug the structure of items to see if Key Documents contains proper routeName values

    const location = useLocation();
    const navigate = useNavigate();
    const currentPath = location.pathname.substring(1); // Get current path without leading slash
    return (
        <SidebarGroup>
            <SidebarMenu>
                {items.map((item) => (
                    <Collapsible key={item.title} asChild defaultOpen={item.isActive || isGroupActive(item.items, currentPath)}>
                        <SidebarMenuItem>
                            {item.items?.length ? (
                                <CollapsibleTrigger asChild>
                                    <SidebarMenuButton asChild tooltip={item.title} isActive={isGroupActive(item.items, currentPath)}>
                                        <button type="button" className="w-full flex items-center gap-2 text-left" data-sentry-unmask>
                                            {React.isValidElement(item.icon) ?
                                                React.cloneElement(item.icon, { className: "text-nav-title" } as any) :
                                                React.createElement(item.icon || (() => <FontAwesomeIcon icon={faFileAlt} />), { className: "text-nav-title" })}
                                            <SidebarTooltip content={item.title} clickable={true} className="text-nav-title" data-sentry-unmask>
                                                {item.shortTitle || item.title}
                                            </SidebarTooltip>
                                        </button>
                                    </SidebarMenuButton>
                                </CollapsibleTrigger>
                            ) : (
                                <SidebarMenuButton asChild>
                                    <button type="button" className="w-full flex items-center gap-2 text-left" 
                                        onClick={(e) => {
                                            handleNavigationWithPaywall(item.url, navigate, e, item.routeName);
                                        }}
                                        onAuxClick={(e) => {
                                            // Middle click
                                            handleNavigationWithPaywall(item.url, navigate, e, item.routeName);
                                        }}
                                        onContextMenu={(e) => {
                                            // Right click - prevent context menu and open in new tab
                                            e.preventDefault();
                                            handleNavigationWithPaywall(item.url, navigate, e, item.routeName);
                                        }}
                                        data-sentry-unmask>
                                        {item.icon && (
                                            React.isValidElement(item.icon) ? item.icon : React.createElement(item.icon || (() => <FontAwesomeIcon icon={faFileAlt} />))
                                        )}
                                        <SidebarTooltip content={item.title} clickable={true} data-sentry-unmask>
                                            {item.shortTitle || item.title}
                                        </SidebarTooltip>
                                    </button>
                                </SidebarMenuButton>
                            )}
                            {item.items?.length ? (
                                <>
                                    <CollapsibleTrigger asChild>
                                        <SidebarMenuAction className="data-[state=open]:rotate-90 transition-transform duration-200">
                                            <ChevronRight />
                                            <span className="sr-only">Toggle</span>
                                        </SidebarMenuAction>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <SidebarMenuSub>
                                            {item.items.map((subItem) => (
                                                <SidebarMenuSubItem key={subItem.title}>
                                                    {/* Check if this subItem has its own nested items */}
                                                    {subItem.items?.length ? (
                                                        <Collapsible asChild defaultOpen={isGroupActive(subItem.items, currentPath)}>
                                                            <div>
                                                                {/* Collapsible header - Use the same layout as top-level items */}
                                                                <div className="relative">
                                                                    <SidebarMenuSubButton asChild isActive={isMenuItemActive(subItem.url, currentPath) || isGroupActive(subItem.items, currentPath)}>
                                                                        <button type="button" className="w-full flex items-center gap-2 text-left" onClick={(e) => {
                                                                            handleNavigationWithPaywall(subItem.url, navigate, e, subItem.routeName);
                                                                        }}
                                                                        data-sentry-unmask>
    {subItem.icon && (
        React.isValidElement(subItem.icon) ?
        subItem.icon :
        React.createElement(subItem.icon || (() => <FontAwesomeIcon icon={faFileAlt} />))
    )}
    <SidebarTooltip content={subItem.title} clickable={true} data-sentry-unmask>
        {subItem.shortTitle || subItem.title}
    </SidebarTooltip>
</button>
                                                                    </SidebarMenuSubButton>
                                                                    <CollapsibleTrigger asChild>
                                                                        <SidebarMenuAction className="data-[state=open]:rotate-90 transition-transform duration-200">
                                                                            <ChevronRight />
                                                                            <span className="sr-only">Toggle</span>
                                                                        </SidebarMenuAction>
                                                                    </CollapsibleTrigger>
                                                                </div>
                                                                {/* Nested submenu */}
                                                                <CollapsibleContent>
                                                                    <SidebarMenuSub className="pl-4 mt-1">
                                                                        {subItem.items.map((nestedItem) => (
                                                                            <SidebarMenuSubItem key={nestedItem.title}>
                                                                                <SidebarMenuSubButton asChild isActive={isMenuItemActive(nestedItem.url, currentPath)}>
                                                                                    <button type="button" className="w-full flex items-center gap-2 text-left" onClick={(e) => {
                                                                                        handleNavigationWithPaywall(nestedItem.url, navigate, e, nestedItem.routeName);
                                                                                    }}
                                                                                    onAuxClick={(e) => {
                                                                                        // Middle click
                                                                                        handleNavigationWithPaywall(nestedItem.url, navigate, e, nestedItem.routeName);
                                                                                    }}
                                                                                    onContextMenu={(e) => {
                                                                                        // Right click - prevent context menu and open in new tab
                                                                                        e.preventDefault();
                                                                                        handleNavigationWithPaywall(nestedItem.url, navigate, e, nestedItem.routeName);
                                                                                    }}
                                                                                    data-sentry-unmask>
    {nestedItem.icon && (
        React.isValidElement(nestedItem.icon) ?
        nestedItem.icon :
        React.createElement(nestedItem.icon || (() => <FontAwesomeIcon icon={faFileAlt} />))
    )}
    <SidebarTooltip content={nestedItem.title} clickable={true} data-sentry-unmask>
        {nestedItem.shortTitle || nestedItem.title}
    </SidebarTooltip>
</button>
                                                                                </SidebarMenuSubButton>
                                                                            </SidebarMenuSubItem>
                                                                        ))}
                                                                    </SidebarMenuSub>
                                                                </CollapsibleContent>
                                                            </div>
                                                        </Collapsible>
                                                    ) : (
                                                        <SidebarMenuSubButton asChild isActive={isMenuItemActive(subItem.url, currentPath)}>
                                                            <button type="button" className="w-full flex items-center gap-2 text-left" onClick={(e) => {
                                                                handleNavigationWithPaywall(subItem.url, navigate, e, subItem.routeName);
                                                            }}
                                                            onAuxClick={(e) => {
                                                                // Middle click
                                                                handleNavigationWithPaywall(subItem.url, navigate, e, subItem.routeName);
                                                            }}
                                                            onContextMenu={(e) => {
                                                                // Right click - prevent context menu and open in new tab
                                                                e.preventDefault();
                                                                handleNavigationWithPaywall(subItem.url, navigate, e, subItem.routeName);
                                                            }}
                                                            data-sentry-unmask>
                                                                {subItem.icon && (
                                                                    React.isValidElement(subItem.icon) ? 
                                                                    subItem.icon : 
                                                                    React.createElement(subItem.icon || (() => <FontAwesomeIcon icon={faFileAlt} />))
                                                                )}
                                                                <SidebarTooltip content={subItem.title} clickable={true} data-sentry-unmask>
                                                                    {subItem.shortTitle || subItem.title}
                                                                </SidebarTooltip>
                                                            </button>
                                                        </SidebarMenuSubButton>
                                                    )}
                                                </SidebarMenuSubItem>
                                            ))}
                                        </SidebarMenuSub>
                                    </CollapsibleContent>
                                </>
                            ) : null}
                        </SidebarMenuItem>
                    </Collapsible>
                ))}
            </SidebarMenu>
        </SidebarGroup>
    )
}

export { ManagementSystem }
